import { FC, Fragment, PropsWithChildren } from "react";
import { Match as MatchType } from "./types/football_table";
import classNames from "classnames";
import { THEME, THEME_LIVE } from "./config";
import Flag from "./flag";
import AH from "./markets/ah";
import { convertOdds } from "./convertOdds";
import { cleanCornerId } from "./helper";
import { CONFIG } from "../../config";
import { useTranslation } from "react-i18next";
import { ShareIcon, Monitoring, LiveTVV2 } from "./icons";

export type MatchProps = {
  oddsType?: number;
  sport?: string;
  tableType?: string;
  isFlagVisible?: boolean;
  isLive?: boolean;
  hasTips?: boolean;
  hasMl?: boolean;
  data?: Partial<MatchType>;
  className?: string;
  isSpecialOpen?: boolean;
  date?: string;
  pvd?: string;
  onClickMore?: (matchId: string) => void;
  onClickPrice?: (data: {
    outcomeId: string;
    marketId: string;
    sp: string;
    matchId: string;
    leagueId: string;
    option: number;
  }) => void;
  onClickShare?: () => void;
};

const Match: FC<PropsWithChildren<MatchProps>> = ({
  oddsType = 0,
  isLive = false,
  hasMl = false,
  isFlagVisible = false,
  data,
  className,
  date,
  hasTips = false,
  tableType = "live-today",
  sport = "football",
  isSpecialOpen = false,
  onClickPrice,
  onClickMore,
  children,
  onClickShare,
}) => {
  const theme = isLive ? THEME_LIVE : THEME;
  const mlKey = hasMl ? "ml" : "x12";
  const ml1stKey = hasMl ? "ml1st" : "x121st";
  const { t, i18n } = useTranslation();

  return (
    <Fragment>
      {!data && (
        <tr className={classNames(theme.borderColor, "border-t", className)}>
          <td colSpan={20} className="align-top p-1 font-semibold text-center">
            No competition
          </td>
        </tr>
      )}
      {data?.bet_prices?.map((betPrice, index) => {
        return (
          <tr
            key={index}
            className={classNames(theme.borderColor, "border-t", className)}>
            {index === 0 && (
              <td
                className="align-top p-1 font-semibold"
                rowSpan={index === 0 ? data.bet_prices?.length : 1}>
                {index === 0 && (
                  <>
                    <div
                      className={classNames("text-center", {
                        "text-red-600": isLive,
                        "animate-pulse": isLive,
                      })}>
                      {date}
                    </div>
                    <div className="flex items-center justify-center gap-x-1">
                      {!!data.st && (
                        <a href={data.st} target="_blank" rel="noreferrer">
                          <Monitoring />
                        </a>
                      )}
                      {data.isChannel && !!data?.id && (
                        <a
                          href={`https://askmebet.live/en/match/${cleanCornerId(
                            data?.id,
                          )}`}
                          target="_blank"
                          rel="noreferrer">
                          <LiveTVV2 />
                        </a>
                      )}
                      {onClickShare && (
                        <button type="button" onClick={onClickShare}>
                          <ShareIcon />
                        </button>
                      )}
                    </div>

                    {CONFIG.ENV === "develop" && (
                      <div className="text-center">
                        {!!data.provider && (
                          <p
                            className={classNames("font-bold", {
                              "text-pink-600": data.provider === "sbo",
                              "text-blue-600": data.provider === "ibc",
                              "text-green-600": data.provider === "bti",
                              "text-purple-700": data.provider === "ufa",
                            })}>
                            {`${data.provider}`.toUpperCase()}
                          </p>
                        )}
                      </div>
                    )}

                    {hasTips && (
                      <a
                        rel="noreferrer"
                        href={`https://askmetips.com/${i18n.language}/${sport}/${data.id}?link=${CONFIG.BASE_URL}/${sport}/${tableType}?matchId=${data.id}`}
                        target="_blank">
                        <img src="/askmetips.gif" alt="tips" />
                      </a>
                    )}
                  </>
                )}
              </td>
            )}
            {index === 0 && (
              <td
                className="align-top p-1 font-semibold"
                rowSpan={index === 0 ? data?.bet_prices?.length : 1}>
                {index === 0 && (
                  <div>
                    <div className="flex gap-x-2">
                      {isLive && <b>{data.home_score}</b>}
                      {isFlagVisible && (
                        <Flag
                          className="mt-1"
                          src={data?.home_flag_img}
                          flagId={`${data.home_flag_id}`}
                        />
                      )}
                      <span
                        className={classNames({
                          "text-red-600": `${betPrice?.ah?.home_hdp}`.includes(
                            "-",
                          ),
                          "text-blue-700":
                            !`${betPrice?.ah?.home_hdp}`.includes("-"),
                        })}>
                        {data.home}
                      </span>
                      {Boolean(Number(data?.home_hrc)) && (
                        <span className="ml-2">
                          {Array.from({
                            length: Number(data?.home_hrc),
                          }).map((_each, index) => (
                            <img
                              key={index}
                              className="inline"
                              src="/red.png"
                              alt="red-card"
                              width={20}
                            />
                          ))}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-x-2">
                      {isLive && <b>{data.away_score}</b>}
                      {isFlagVisible && (
                        <Flag
                          className="mt-1"
                          src={data?.away_flag_img}
                          flagId={`${data.away_flag_id}`}
                        />
                      )}
                      <span
                        className={classNames({
                          "text-red-600": `${betPrice?.ah?.away_hdp}`.includes(
                            "-",
                          ),
                          "text-blue-700":
                            !`${betPrice?.ah?.away_hdp}`.includes("-"),
                        })}>
                        {data.away}
                      </span>
                      {Boolean(Number(data?.away_arc)) && (
                        <span className="ml-2">
                          {Array.from({
                            length: Number(data?.away_arc),
                          }).map((_each, index) => (
                            <img
                              key={index}
                              className="inline"
                              src="/red.png"
                              alt="red-card"
                              width={20}
                            />
                          ))}
                        </span>
                      )}
                    </div>
                    {!hasMl && (
                      <div
                        className={classNames({
                          "pl-10": isFlagVisible && isLive,
                          "pl-6": isFlagVisible && !isLive,
                          "pl-4": !isFlagVisible && isLive,
                        })}>
                        {t("Draw")}
                      </div>
                    )}
                  </div>
                )}
              </td>
            )}

            <td className="align-top p-1 relative">
              <div className="mx-auto" style={{ width: 90 }}>
                {!!betPrice.ah?.home_price && (
                  <AH
                    minParlay={betPrice.ah?.min_parlay}
                    option={0}
                    onClick={onClickPrice}
                    sp={betPrice?.ah?.sp || ""}
                    outcomeId={betPrice?.ah?.home_outcome_id || ""}
                    marketId={betPrice?.ah?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    hdp={
                      (
                        `${betPrice?.ah?.home_hdp}`?.includes("-") ||
                        `${betPrice?.ah?.home_hdp}` === "0"
                      ) ?
                        `${betPrice?.ah?.home_hdp}`?.replace("-", "")
                      : " "
                    }
                    price={convertOdds(betPrice.ah?.home_price, oddsType)}
                  />
                )}
                {!!betPrice.ah?.away_price && (
                  <AH
                    option={1}
                    onClick={onClickPrice}
                    sp={betPrice?.ah?.sp || ""}
                    outcomeId={betPrice?.ah?.away_outcome_id || ""}
                    marketId={betPrice?.ah?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    hdp={
                      `${betPrice?.ah?.away_hdp}`?.includes("-") ?
                        `${betPrice?.ah?.away_hdp}`?.replace("-", "")
                      : " "
                    }
                    price={convertOdds(betPrice.ah?.away_price, oddsType)}
                  />
                )}
              </div>
            </td>
            <td className="align-top p-1 relative">
              <div className="mx-auto" style={{ width: 110 }}>
                {!!betPrice.ou?.over_price && (
                  <AH
                    minParlay={betPrice.ou?.min_parlay}
                    option={0}
                    onClick={onClickPrice}
                    sp={betPrice?.ou?.sp || ""}
                    outcomeId={betPrice?.ou?.over_outcome_id || ""}
                    marketId={betPrice?.ou?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    label="O"
                    hdp={betPrice?.ou?.over_hdp}
                    price={convertOdds(betPrice.ou?.over_price, oddsType)}
                  />
                )}
                {!!betPrice.ou?.under_price && (
                  <AH
                    minParlay={betPrice.ou?.min_parlay}
                    option={1}
                    onClick={onClickPrice}
                    sp={betPrice?.ou?.sp || ""}
                    outcomeId={betPrice?.ou?.under_outcome_id || ""}
                    marketId={betPrice?.ou?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    label="U"
                    hdp=" "
                    price={convertOdds(betPrice.ou?.under_price, oddsType)}
                  />
                )}
              </div>
            </td>
            <td className="align-top p-1 relative">
              <div className="pr-1 mx-auto" style={{ width: 50 }}>
                {!!betPrice?.[mlKey]?.home_price && (
                  <AH
                    minParlay={betPrice?.[mlKey]?.min_parlay}
                    option={0}
                    onClick={onClickPrice}
                    sp={betPrice?.[mlKey]?.sp || ""}
                    outcomeId={betPrice?.[mlKey]?.home_outcome_id || ""}
                    marketId={betPrice?.[mlKey]?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    price={convertOdds(betPrice?.[mlKey]?.home_price, oddsType)}
                  />
                )}
                {!!betPrice?.[mlKey]?.away_price && (
                  <AH
                    minParlay={betPrice?.[mlKey]?.min_parlay}
                    option={1}
                    onClick={onClickPrice}
                    sp={betPrice?.[mlKey]?.sp || ""}
                    outcomeId={betPrice?.[mlKey]?.away_outcome_id || ""}
                    marketId={betPrice?.[mlKey]?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    price={convertOdds(betPrice?.[mlKey]?.away_price, oddsType)}
                  />
                )}

                {!hasMl && !!betPrice?.[mlKey]?.draw_price && (
                  <AH
                    minParlay={betPrice?.[mlKey]?.min_parlay}
                    option={2}
                    onClick={onClickPrice}
                    sp={betPrice?.[mlKey]?.sp || ""}
                    outcomeId={betPrice?.[mlKey]?.draw_outcome_id || ""}
                    marketId={betPrice?.[mlKey]?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    price={convertOdds(betPrice?.[mlKey]?.draw_price, oddsType)}
                  />
                )}
              </div>
            </td>

            <td className="align-top p-1 relative">
              <div className="mx-auto" style={{ width: 80 }}>
                {!!betPrice.oe?.odd_price && (
                  <AH
                    minParlay={betPrice?.oe?.min_parlay}
                    option={0}
                    onClick={onClickPrice}
                    sp={betPrice?.oe?.sp || ""}
                    outcomeId={betPrice?.oe?.odd_outcome_id || ""}
                    marketId={betPrice?.oe?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    label="O"
                    price={convertOdds(betPrice.oe?.odd_price, oddsType)}
                  />
                )}
                {!!betPrice.oe?.even_price && (
                  <AH
                    minParlay={betPrice?.oe?.min_parlay}
                    option={1}
                    onClick={onClickPrice}
                    sp={betPrice?.oe?.sp || ""}
                    outcomeId={betPrice?.oe?.even_outcome_id || ""}
                    marketId={betPrice?.oe?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    label="E"
                    price={convertOdds(betPrice.oe?.even_price, oddsType)}
                  />
                )}
              </div>
            </td>

            <td className="align-top p-1 relative">
              <div className="mx-auto" style={{ width: 90 }}>
                {!!betPrice.ah1st?.home_price && (
                  <AH
                    minParlay={betPrice?.ah1st?.min_parlay}
                    option={0}
                    onClick={onClickPrice}
                    sp={betPrice?.ah1st?.sp || ""}
                    outcomeId={betPrice?.ah1st?.home_outcome_id || ""}
                    marketId={betPrice?.ah1st?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    hdp={
                      (
                        `${betPrice?.ah1st?.home_hdp}`?.includes("-") ||
                        `${betPrice?.ah1st?.home_hdp}` === "0"
                      ) ?
                        `${betPrice?.ah1st?.home_hdp}`?.replace("-", "")
                      : " "
                    }
                    price={convertOdds(betPrice.ah1st?.home_price, oddsType)}
                  />
                )}
                {!!betPrice.ah1st?.away_price && (
                  <AH
                    minParlay={betPrice?.ah1st?.min_parlay}
                    option={1}
                    onClick={onClickPrice}
                    sp={betPrice?.ah1st?.sp || ""}
                    outcomeId={betPrice?.ah1st?.away_outcome_id || ""}
                    marketId={betPrice?.ah1st?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    hdp={
                      `${betPrice?.ah1st?.away_hdp}`?.includes("-") ?
                        `${betPrice?.ah1st?.away_hdp}`?.replace("-", "")
                      : " "
                    }
                    price={convertOdds(betPrice.ah1st?.away_price, oddsType)}
                  />
                )}
              </div>
            </td>
            <td className="align-top p-1 relative">
              <div className="mx-auto" style={{ width: 105 }}>
                {!!betPrice.ou1st?.over_price && (
                  <AH
                    minParlay={betPrice?.ou1st?.min_parlay}
                    option={0}
                    onClick={onClickPrice}
                    sp={betPrice?.ou1st?.sp || ""}
                    outcomeId={betPrice?.ou1st?.over_outcome_id || ""}
                    marketId={betPrice?.ou1st?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    label="O"
                    hdp={betPrice?.ou1st?.over_hdp}
                    price={convertOdds(betPrice.ou1st?.over_price, oddsType)}
                  />
                )}

                {!!betPrice.ou1st?.under_price && (
                  <AH
                    minParlay={betPrice?.ou1st?.min_parlay}
                    option={1}
                    onClick={onClickPrice}
                    sp={betPrice?.ou1st?.sp || ""}
                    outcomeId={betPrice?.ou1st?.under_outcome_id || ""}
                    marketId={betPrice?.ou1st?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    label="U"
                    hdp=" "
                    price={convertOdds(betPrice.ou1st?.under_price, oddsType)}
                  />
                )}
              </div>
            </td>
            <td className="p-1 relative">
              <div className="pr-1 mx-auto" style={{ width: 50 }}>
                {!!betPrice?.[ml1stKey]?.home_price && (
                  <AH
                    minParlay={betPrice?.[ml1stKey]?.min_parlay}
                    option={0}
                    onClick={onClickPrice}
                    sp={betPrice?.[ml1stKey]?.sp || ""}
                    outcomeId={betPrice?.[ml1stKey]?.home_outcome_id || ""}
                    marketId={betPrice?.[ml1stKey]?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    price={convertOdds(
                      betPrice?.[ml1stKey]?.home_price,
                      oddsType,
                    )}
                  />
                )}
                {!!betPrice?.[ml1stKey]?.away_price && (
                  <AH
                    minParlay={betPrice?.[ml1stKey]?.min_parlay}
                    option={1}
                    onClick={onClickPrice}
                    sp={betPrice?.[ml1stKey]?.sp || ""}
                    outcomeId={betPrice?.[ml1stKey]?.away_outcome_id || ""}
                    marketId={betPrice?.[ml1stKey]?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    price={convertOdds(
                      betPrice?.[ml1stKey]?.away_price,
                      oddsType,
                    )}
                  />
                )}
                {!!betPrice?.[ml1stKey]?.draw_price && (
                  <AH
                    minParlay={betPrice?.[ml1stKey]?.min_parlay}
                    option={2}
                    onClick={onClickPrice}
                    sp={betPrice?.[ml1stKey]?.sp || ""}
                    outcomeId={betPrice?.[ml1stKey]?.draw_outcome_id || ""}
                    marketId={betPrice?.[ml1stKey]?.market_id || ""}
                    matchId={`${data.id}`}
                    leagueId={`${data.leagueId}`}
                    price={convertOdds(
                      betPrice?.[ml1stKey]?.draw_price,
                      oddsType,
                    )}
                  />
                )}
              </div>
            </td>

            <td className="align-top px-1 pt-2 font-semibold text-center">
              {!CONFIG.IS_DISABLED_SPECIAL &&
                !!data.special_prices_no &&
                index === 0 && (
                  <button
                    onClick={() => {
                      if (isSpecialOpen) {
                        onClickMore?.("");
                      } else {
                        onClickMore?.(`${data.id}`);
                      }
                    }}
                    style={{ minWidth: 20 }}
                    className="bg-gray-300 py-1 text-black rounded px-1 font-semibold hover:cursor-pointer">
                    {isSpecialOpen ? "-" : "+"}
                    {data.special_prices_no}
                  </button>
                )}
            </td>
          </tr>
        );
      })}
      {children}
    </Fragment>
  );
};

export default Match;
